/**
 * The hole an opening carves out of the face of its wall: how far along the
 * face (`alongWallSpan`, the opening's own rotated footprint) and how far up
 * it (`cutsDownToFloor` + `openingHoleSpanY` — a door to the floor of its own
 * face, a window on its sill, a styled door's frame on top).
 *
 * ONE rule for everyone who needs that hole: the 3D carve (`PolygonWalls`),
 * and the elevation drawing, which cuts every finish laid on a wall at its
 * doors and windows by it, so a tile band drawn on paper stops where the 3D
 * tile does (Yu-Han, 2026-10-22). Pure.
 */
import { alongWallSpan } from './wallFaceCarve';
import { cutsDownToFloor, openingHoleSpanY } from './coplanarOpenings';
import { isDemoedOpening } from '../../../utils/parentWallHelpers';

/** A styled door's frame stands this far above its opening (frames are 6 cm,
 * plus a margin); the hole runs up to it. A plain door needs none. */
const STYLED_DOOR_TOP_BUFFER_M = 0.08;

/**
 * How much higher than the opening its hole runs: a styled door's frame.
 * The style is read flat from `category.door` (Phase 5 hoist), falling back
 * to the legacy nested `doorStyle` for unmigrated structures.
 */
export function styledDoorTopBuffer(opening, type) {
  if (type !== 'door') return 0;
  const doorMeta = opening?.category?.door;
  const style = doorMeta?.style ?? doorMeta?.doorStyle?.style;
  return style && style !== 'none' ? STYLED_DOOR_TOP_BUFFER_M : 0;
}

const dot3 = (a, b) => (a.x || 0) * b.x + (a.y || 0) * b.y + (a.z || 0) * b.z;

/**
 * The hole `opening` cuts in a face, in that face's frame.
 *
 * @param {object} opening a HYDRATED opening (`center`, `dimensions`)
 * @param {object} face
 * @param {'door'|'window'|'opening'} face.type what the opening is
 * @param {{x, y, z}} face.right along the face
 * @param {{x, y, z}} face.up up the face
 * @param {{x, y, z}} face.origin where `left` / `right` and `centerV` are
 *   measured from
 * @param {number} face.faceMinY the face's lowest point, on `up` from `origin`
 * @param {object} [face.wallRotation] for an opening with no rotation of its
 *   own: parallel to its wall
 * @param {boolean} [face.reachedByCoplanarBridge] the opening is another
 *   body's, carved here only because it shares the plane — it never snaps to
 *   this face's floor
 * @returns {?{left, right, bottom, top}} null for an opening with no geometry
 */
export function openingHole(
  opening,
  {
    type,
    right,
    up,
    origin,
    faceMinY,
    wallRotation,
    reachedByCoplanarBridge = false,
  }
) {
  const { dimensions, center } = opening || {};
  if (!dimensions || !center) return null;
  const span = alongWallSpan(
    {
      center,
      dimensions,
      // Every opening in the corpus carries its own rotation; if one ever
      // doesn't, assume it is parallel to its wall.
      rotation: opening.rotation ?? wallRotation,
    },
    { x: right.x, z: right.z },
    { x: origin.x, z: origin.z }
  );
  const openingHeight = dimensions.y || 2.0;
  const centerV =
    dot3(center, up) - dot3({ x: origin.x, y: origin.y, z: origin.z }, up);
  const cutsToFloor = cutsDownToFloor({
    type,
    isDemoed: isDemoedOpening(opening),
    reachedByCoplanarBridge,
    sillAboveFaceFloor: centerV - openingHeight / 2 - faceMinY,
    opening,
  });
  const { bottom, top } = openingHoleSpanY({
    cutsToFloor,
    faceMinY,
    centerV,
    openingHeight,
    topBuffer: styledDoorTopBuffer(opening, type),
  });
  return { left: span.min, right: span.max, bottom, top };
}
